import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  partnerApi,
  PartnerApiError,
  setPartnerMfaSetupHandler,
  setPartnerUnauthenticatedHandler,
} from '../api/partner.ts';
import { AFFILIATE_EARNING_STATUSES } from '@shared/constants/affiliate.ts';
import { Alert, Button, ConfirmDialog, LoadingBlock } from './ui.tsx';
import { AppShell, type ShellNavGroup, type ShellNavItem } from './app-shell.tsx';

/**
 * Shell for the affiliate partner portal.
 *
 * English-only, like the operator console and unlike the tenant app: a partner
 * account is a commercial relationship with this deployment, not a customer
 * surface, so none of it goes through i18next.
 *
 * The gate here is UX, not security. `/api/partner/*` refuses everything
 * without a partner session whatever the SPA renders, and the mandate — where
 * a deployment turns one on — is enforced by `requirePartnerMfaSatisfied` in
 * the Worker.
 */

export const PARTNER_SESSION_QUERY_KEY = ['partner', 'session'] as const;

export function usePartnerIdentity() {
  return useQuery({
    queryKey: PARTNER_SESSION_QUERY_KEY,
    queryFn: partnerApi.session,
    // A signed-out visitor asking who they are is a normal answer, not a
    // failure worth repeating.
    retry: (failureCount, error) => !(error instanceof PartnerApiError && error.status === 401) && failureCount < 2,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Whether this partner may still ACT, as opposed to merely read.
 *
 * Read from the shared set the Worker's `requireActivePartner` uses, not from
 * `status === 'active'`: a partner under `payout_hold` or `compliance_review`
 * keeps their write routes on purpose, and a screen that grays the button out
 * anyway refuses something the API would have allowed.
 */
export function partnerMayAct(status: string | undefined): boolean {
  return AFFILIATE_EARNING_STATUSES.includes(status as (typeof AFFILIATE_EARNING_STATUSES)[number]);
}

/**
 * The portal's only redirect out of itself, and it fires on exactly one
 * condition: the server said 401.
 *
 * That narrowness is the point. The sign-in page sends a partner INTO the
 * portal only when the same query SUCCEEDS, so the two can never take turns —
 * one resolution of one shared query satisfies at most one of them. A network
 * failure or a 500 is neither: it renders a message with a retry, because a
 * redirect on "could not tell" is precisely the loop that leaves a browser
 * flashing between two screens.
 */
export function RequirePartner({ children }: { children: ReactNode }) {
  const identity = usePartnerIdentity();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Any 401 from a page BELOW this guard means the session ended underneath
  // it. Re-asking the session question is what turns that into a redirect —
  // otherwise the shell keeps rendering around screens that each fail on
  // their own, because a successful answer going stale never refetches by
  // itself.
  useEffect(() => {
    setPartnerUnauthenticatedHandler(() => {
      // Removed rather than invalidated: the retained successful value would
      // otherwise sit next to the new error and read as "still signed in" to
      // anything checking `data`.
      queryClient.removeQueries({ queryKey: ['partner'] });
    });
    // The mandate's 403 is the other half: re-ask the session question, whose
    // `setupRequired` then routes to the security screen below. Refetching
    // rather than removing, because the session itself is perfectly valid.
    setPartnerMfaSetupHandler(() => {
      void queryClient.refetchQueries({ queryKey: PARTNER_SESSION_QUERY_KEY });
    });
    return () => {
      setPartnerUnauthenticatedHandler(null);
      setPartnerMfaSetupHandler(null);
    };
  }, [queryClient]);

  if (identity.isLoading) return <LoadingBlock label="Checking your partner session…" />;
  if (identity.error instanceof PartnerApiError && identity.error.status === 401) {
    return <Navigate to="/partner/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (identity.isError || !identity.data) {
    return (
      <main className="mx-auto max-w-md px-4 py-24">
        <Alert tone="error" title="The partner portal could not be reached">
          {identity.error instanceof Error ? identity.error.message : 'Please try again in a moment.'}
        </Alert>
        <Button className="mt-4" onClick={() => void identity.refetch()}>
          Try again
        </Button>
      </main>
    );
  }
  return <>{children}</>;
}

/**
 * The portal's navigation, in the app shell every audience shares
 * (components/app-shell.tsx): the same sidebar, rail and phone bar, the same
 * order — Overview alone at the top, the labelled groups, then the account
 * pinned at the foot. The groups keep the office's names where the meaning
 * matches: the customers a partner brought in (Sales), and what they earned
 * for it (Business).
 */
const NAV_ITEMS: ReadonlyArray<ShellNavItem> = [
  { to: '/partner', label: 'Overview', icon: 'home', group: 'home' },
  { to: '/partner/customers', label: 'Customers', icon: 'users', group: 'sales' },
  { to: '/partner/claims', label: 'Attribution claims', icon: 'inspection', group: 'sales' },
  { to: '/partner/commissions', label: 'Commissions', icon: 'chart', group: 'business' },
  { to: '/partner/payouts', label: 'Payouts', icon: 'invoice', group: 'business' },
  { to: '/partner/account', label: 'Account', icon: 'settings', group: 'footer' },
  { to: '/partner/security', label: 'Security', icon: 'shield', group: 'footer' },
];

const NAV_GROUPS: ReadonlyArray<ShellNavGroup> = [
  { id: 'sales', label: 'Sales' },
  { id: 'business', label: 'Business' },
];

const PHONE_TAB_ORDER: ReadonlyArray<string> = [
  '/partner',
  '/partner/customers',
  '/partner/commissions',
  '/partner/payouts',
];

const BRAND = { to: '/partner', label: 'Partner portal' };

export default function PartnerLayout() {
  const identity = usePartnerIdentity();
  const location = useLocation();
  const queryClient = useQueryClient();
  const data = identity.data;
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);

  async function handleSignOut() {
    setSigningOut(true);
    setSignOutError(null);
    try {
      await partnerApi.logout();
    } catch (caught) {
      // A 401 means there was no live session to end, which is what was
      // wanted. Anything else — offline, a refused CSRF token, a Worker error —
      // means the HttpOnly cookie and the server-side session are both still
      // live. Leaving for the sign-in page then would show a sign-out that did
      // not happen, and on a shared device the next person to reload this tab
      // would be signed straight back in. So the dialog stays up with the
      // error, and the retry is the same button.
      if (!(caught instanceof PartnerApiError && caught.status === 401)) {
        setSigningOut(false);
        setSignOutError(
          caught instanceof PartnerApiError
            ? `Could not sign you out: ${caught.message}`
            : 'Could not sign you out. Check your connection and try again.',
        );
        return;
      }
    }
    queryClient.removeQueries({ queryKey: ['partner'] });
    // A hard navigation, so every partner query resets alongside the cookies.
    window.location.assign('/partner/login');
  }

  // The ONE place the portal moves somebody against their wishes, and only
  // where the deployment actually mandates a factor (`mfa_require_partner`,
  // which ships off). Two-factor authentication is otherwise entirely optional
  // here: a partner without one gets the whole portal and an invitation, not a
  // wall. The security screen itself is excluded, or the state would have no
  // exit.
  const onSecurity = location.pathname.startsWith('/partner/security');
  if (data?.mfa.setupRequired && !onSecurity) return <Navigate to="/partner/security?setup=1" replace />;

  return (
    <AppShell
      items={NAV_ITEMS}
      groups={NAV_GROUPS}
      phoneTabOrder={PHONE_TAB_ORDER}
      brand={BRAND}
      moreOpen={moreOpen}
      onMoreOpenChange={setMoreOpen}
      mainClassName="mx-auto max-w-6xl px-4 py-6"
      topBar={
        <>
          <p className="min-w-0 flex-1 basis-0 truncate text-lg font-bold text-[color:var(--color-brand)]">
            Partner portal
          </p>
          <div className="ml-auto flex shrink-0 items-center gap-3">
            <span className="hidden text-sm text-[color:var(--color-muted)] sm:inline">{data?.user.email}</span>
            {/* Asked first, for the same reason the operator console asks: the
                sign-out is a hard navigation, so a mis-hit costs the page the
                partner was reading as well as the session. */}
            <Button variant="ghost" busy={signingOut} onClick={() => setConfirmingSignOut(true)}>
              Sign out
            </Button>
            {confirmingSignOut ? (
              <ConfirmDialog
                title="Sign out of the partner portal?"
                confirmLabel="Sign out"
                cancelLabel="Stay signed in"
                busy={signingOut}
                error={signOutError}
                onCancel={() => {
                  setConfirmingSignOut(false);
                  setSignOutError(null);
                }}
                onConfirm={() => void handleSignOut()}
              >
                <p>This ends your partner session on this device and returns you to the sign-in page.</p>
              </ConfirmDialog>
            ) : null}
          </div>
        </>
      }
    >
      {data && !partnerMayAct(data.partner.status) ? (
        <div className="mb-4">
          <Alert tone="warning" title={`Your partner account is ${data.partner.status.replace(/_/g, ' ')}`}>
            Your figures stay visible. Submitting attribution claims and changing your account details are paused until
            the account is active again.
          </Alert>
        </div>
      ) : null}
      <Outlet />
    </AppShell>
  );
}
